import { LitElement } from '@lit'

class NovaAccountWrapper extends LitElement {
  static properties = {
    breakpoint: { type: Number },
  }

  constructor() {
    super()
    this.breakpoint = 990 // Align with CSS breakpoint
  }

  firstUpdated() {
    this.initializeTabs()
    this.initializeEmailValidation()
    this.initializePasswordValidation()
    this.initializeNameValidation()
    this.setupLoginButtonListeners()
    this.handleInitialHash()
  }

  initializeTabs() {
    const tabs = this.querySelectorAll('.js-tab')
    const panels = this.querySelectorAll('.js-tabpanel')

    for (const tab of tabs) {
      tab.addEventListener('click', () => {
        this.switchTab(panels, tabs, tab)
      })
    }
  }

  initializeNameValidation() {
    const firstNameInput = this.querySelectorAll('.js-first-name-input')
    const lastNameInput = this.querySelectorAll('.js-last-name-input')

    for (const nameInput of firstNameInput) {
      nameInput.addEventListener('blur', (event) => {
        this.checkName(event, 'first')
      })
    }

    for (const nameInput of lastNameInput) {
      nameInput.addEventListener('blur', (event) => {
        this.checkName(event, 'last')
      })
    }
  }

  initializeEmailValidation() {
    const accountEmailInputs = this.querySelectorAll('.js-email-input')

    for (const emailInput of accountEmailInputs) {
      const inputWrap = emailInput.closest('.js-form-wrapper')
      if (!inputWrap) return
      const emailError = inputWrap.querySelector('.js-email-error')
      const submitButton = inputWrap.querySelector('.js-account-submit')

      const observer = new MutationObserver((mutationsList) => {
        for (const mutation of mutationsList) {
          if (
            mutation.type === 'attributes' &&
            mutation.attributeName === 'data-user-invalid'
          ) {
            if (emailInput.hasAttribute('data-user-invalid')) {
              emailError.classList.remove('hidden')
              submitButton.setAttribute('disabled', 'disabled')
            } else {
              emailError.classList.add('hidden')
              submitButton.removeAttribute('disabled')
            }
          }
        }
      })
      observer.observe(emailInput, {
        attributes: true,
      })
    }
  }

  initializePasswordValidation() {
    const passwordInput =
      this.querySelector('#RegisterForm-password') ||
      this.querySelector('#ResetPasswordForm-password')
    const confirmPasswordInput =
      this.querySelector('#RegisterForm-verify-password') ||
      this.querySelector('#ResetPasswordForm-password-confirmation')
    const submitButton =
      this.querySelector('#RegisterForm-submit') ||
      this.querySelector('#resetpassword-submit')
    const passwordError = this.querySelector('.js-password-error')

    if (passwordInput && confirmPasswordInput) {
      passwordInput.addEventListener('blur', () => {
        this.checkPassword(
          passwordInput,
          confirmPasswordInput,
          submitButton,
          passwordError,
        )
      })

      confirmPasswordInput.addEventListener('blur', () => {
        this.checkPassword(
          passwordInput,
          confirmPasswordInput,
          submitButton,
          passwordError,
        )
      })
    }
  }

  setupLoginButtonListeners() {
    const loginButtons = this.querySelectorAll('.js-login-button')
    for (const button of loginButtons) {
      button.addEventListener('click', (event) => {
        event.preventDefault()
        const targetId = event.target.getAttribute('href').substring(1)
        const currentId = event.target.dataset.current.substring(1)
        const targetElement = this.querySelector(`#${targetId}`)
        const currentElement = this.querySelector(`#${currentId}`)
        targetElement.classList.remove('hidden')
        if (!currentElement.classList.contains('hidden')) {
          currentElement.classList.add('hidden')
        }

        let newUrl

        if (!window.location.href.includes(`#${targetId}`)) {
          newUrl = window.location.href.includes(`#${currentId}`)
            ? window.location.href.replace(`#${currentId}`, `#${targetId}`)
            : `${window.location.href}#${targetId}`
        } else {
          newUrl = window.location.href
        }

        window.history.pushState({}, '', newUrl)
      })
    }
  }

  handleInitialHash() {
    const loadedId = window.location.href.includes('#')
      ? window.location.href.split('#')[1]
      : null
    if (loadedId) {
      const hiddenId = loadedId === 'recover' ? 'login' : 'recover'
      const loadedElement = this.querySelector(`#${loadedId}`)
      const hiddenElement = this.querySelector(`#${hiddenId}`)
      loadedElement.classList.remove('hidden')
      if (!hiddenElement.classList.contains('hidden')) {
        hiddenElement.classList.add('hidden')
      }
    }
  }

  switchTab(panels, tabs, tab) {
    if (window.innerWidth >= this.breakpoint) return

    const tabType = tab.id.replace('-tab', '')
    this.toggleActiveClass(tabs, tabType)
    this.toggleActiveClass(panels, tabType)
    this.updateURL(tabType)
  }

  toggleActiveClass(elements, tabType) {
    for (const element of elements) {
      if (
        element.id.includes(tabType) &&
        !element.classList.contains('mobile-active')
      ) {
        element.classList.add('mobile-active')
      } else if (
        element.id.includes(tabType) &&
        element.classList.contains('mobile-active')
      ) {
        return
      } else {
        element.classList.remove('mobile-active')
      }
    }
  }

  updateURL(tabType) {
    const pathParts = window.location.pathname.split('/')
    if (pathParts.length > 2) {
      pathParts[2] = tabType
    } else {
      pathParts.push(tabType)
    }
    const newUrl = pathParts.join('/')
    history.replaceState(null, '', newUrl)
  }

  checkName(event, nameType) {
    const nameInput = event.target
    const nameValid = nameInput.value.trim() !== '' // Trim whitespace for validation

    const inputWrap = nameInput.closest('.js-form-wrapper')
    if (!inputWrap) return

    const nameErrorClass =
      nameType === 'last' ? '.js-last-name-error' : '.js-first-name-error'
    const nameError = inputWrap.querySelector(nameErrorClass)

    const submitButton = inputWrap.querySelector('.js-account-submit')

    this.toggleErrorClass(inputWrap, !nameValid)

    nameError.classList.toggle('hidden', nameValid)
    submitButton.toggleAttribute('disabled', !nameValid)
  }

  checkPassword(
    passwordInput,
    confirmPasswordInput,
    submitButton,
    passwordError,
  ) {
    if (
      confirmPasswordInput.value === '' ||
      !passwordInput ||
      !confirmPasswordInput
    )
      return

    const passwordsMatch = passwordInput.value === confirmPasswordInput.value
    this.toggleErrorClass(passwordInput, !passwordsMatch)
    this.toggleErrorClass(confirmPasswordInput, !passwordsMatch)

    if (!passwordsMatch) {
      passwordError.classList.remove('hidden')
      submitButton.setAttribute('disabled', 'disabled')
    } else {
      passwordError.classList.add('hidden')
      submitButton.removeAttribute('disabled')
    }
  }

  toggleErrorClass(element, hasError) {
    element.classList.toggle('input-error', hasError)
  }

  createRenderRoot() {
    return this
  }
}

customElements.define('nova-account-wrapper', NovaAccountWrapper)
